// Plan-based feature visibility — 6-tier model
//
// Tier 1 (Individual): free, pro → contracts, documents, templates, settings
// Tier 2 (Business): starter, growth, scale → + clients, invoices, offices, analytics, deal rooms, messages, tasks
// Tier 3 (Enterprise): enterprise → + directory, connect, rfq, products, team management, developer settings

const TIER1_PAGES = ['dashboard', 'deals', 'documents', 'templates', 'settings', 'quick-contract', 'meetings'];
const TIER2_PAGES = [...TIER1_PAGES, 'analytics', 'clients', 'invoices', 'offices', 'virtual-offices', 'company-profile', 'messages', 'tasks'];
const TIER3_PAGES = [...TIER2_PAGES, 'directory', 'connect', 'rfq', 'products', 'developer-settings'];

// Map plan names to their tier level
const PLAN_TO_TIER = {
  'free': 1,
  'basic': 1,       // legacy
  'pro': 1,
  'individual_pro': 1,
  'starter': 2,
  'business_starter': 2,
  'growth': 2,
  'business_pro': 2,  // legacy
  'business_growth': 2,
  'scale': 2,
  'business_scale': 2,
  'enterprise': 3,
};

function getTierPages(tier) {
  switch (tier) {
    case 3: return TIER3_PAGES;
    case 2: return TIER2_PAGES;
    default: return TIER1_PAGES;
  }
}

export function isPageVisible(route, plan) {
  const normalized = route.replace(/^\//, '').split('/')[0].toLowerCase();
  const tier = PLAN_TO_TIER[plan?.toLowerCase()] || 1;
  return getTierPages(tier).includes(normalized);
}

// Check if a specific feature is available for a plan
export function hasFeature(plan, feature) {
  const tier = PLAN_TO_TIER[plan?.toLowerCase()] || 1;
  const featureMap = {
    'ai_contracts': true,        // all tiers
    'guest_signing': true,       // all tiers
    'templates': true,           // all tiers
    'video_calls': tier >= 2,
    'deal_rooms': tier >= 2,
    'analytics': tier >= 2,
    'client_management': tier >= 2,
    'invoicing': tier >= 2,
    'api_access': tier >= 3,
    'white_label': tier >= 3,
    'pdf_branding': tier === 1 && plan?.toLowerCase() === 'free',
  };
  return featureMap[feature] ?? false;
}

// Get the next upgrade tier name
export function getUpgradePlan(plan) {
  const hierarchy = ['free', 'pro', 'starter', 'growth', 'scale', 'enterprise'];
  const normalized = plan?.toLowerCase() || 'free';

  // Map legacy names
  const legacyMap = { 'basic': 'free', 'business_starter': 'starter', 'business_pro': 'growth' };
  const mapped = legacyMap[normalized] || normalized;

  const idx = hierarchy.indexOf(mapped);
  if (idx === -1 || idx >= hierarchy.length - 1) return null;
  return hierarchy[idx + 1];
}

// Plan display names
export const PLAN_DISPLAY_NAMES = {
  free: 'Free',
  pro: 'Pro',
  starter: 'Small Business',
  growth: 'Business Pro',
  scale: 'Scale',
  enterprise: 'Enterprise',
};

// Pricing for display
export const PLAN_PRICING = {
  free:       { monthly: 0,     annualMonthly: 0 },
  pro:        { monthly: 9.99,  annualMonthly: 7.99 },
  starter:    { monthly: 89.99, annualMonthly: 71.99 },
  growth:     { monthly: 149.99, annualMonthly: 119.99 },
  scale:      { monthly: 249.99, annualMonthly: 199.99 },
  enterprise: { monthly: null,  annualMonthly: null },
};

// Full plan details for pricing pages
export const PLAN_DETAILS = [
  { key: 'free', name: 'Free', price: 0, annual: 0, users: '1', contracts: '3/mo', storage: '500MB', description: 'For individuals getting started', highlights: ['AI contract drafting', 'Guest signing', '8 templates'] },
  { key: 'pro', name: 'Pro', price: 9.99, annual: 7.99, users: '1', contracts: '5/mo', storage: '2GB', description: 'For solo professionals', highlights: ['Clean PDFs (no branding)', 'All templates', 'Priority support'] },
  { key: 'starter', name: 'Small Business', price: 89.99, annual: 71.99, users: '3', contracts: '15/mo', storage: '10GB', description: 'For small teams', highlights: ['Deal rooms', 'Video calls', 'Analytics', 'Client management'] },
  { key: 'growth', name: 'Business Pro', price: 149.99, annual: 119.99, users: '15', contracts: '40/mo', storage: '20GB', description: 'For growing businesses', popular: true, highlights: ['Everything in Small Business', 'API access', 'Priority support'] },
  { key: 'scale', name: 'Scale', price: 249.99, annual: 199.99, users: '25', contracts: '100/mo', storage: '200GB', description: 'For large organizations', highlights: ['Everything in Business Pro', 'Unlimited offices', 'Dedicated support'] },
  { key: 'enterprise', name: 'Enterprise', price: null, annual: null, users: 'Unlimited', contracts: 'Unlimited', storage: 'Unlimited', description: 'For complex organizations', highlights: ['White label', '30-day onboarding', 'Custom integrations'] },
];

// Team member limits per plan
export const PLAN_TEAM_LIMITS = {
  free: 1, pro: 1, starter: 3, growth: 15, scale: 25, enterprise: Infinity,
};

// Helper to get display name
export function getPlanDisplayName(planKey) {
  return PLAN_DISPLAY_NAMES[planKey?.toLowerCase()] || (planKey || 'Free').charAt(0).toUpperCase() + (planKey || 'free').slice(1);
}

